import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Lang } from '@/data/types';
import { KBIcon } from '@/components/kbIcons';
import { friendlyError } from '@/lib/friendlyError';
import {
  actesCreateDossier, actesUpdateDossier, actesDossierLifecycle, actesDossiers,
  type ActesDossier, type ActesReference,
} from '@/integrations/actesApi';
import { searchTicketsSmart, fetchTicketById, type TicketMatch } from '@/integrations/hubspot';
import { authHeaders } from '@/lib/authHeaders';
import { prettySubject } from '@/lib/subjects';
import { useTidyName } from '@/hooks/useTidyName';
import { actesEn, dossierStatusLabel } from '@/lib/actesCountry';

/**
 * A dossier, created or corrected.
 *
 * The fields are the ones the old site had, in the same order and with the
 * same meaning — name, the services it covers, its status, the Drive folder,
 * and the two external doors (HubSpot and Parallel) plus the HubSpot order id
 * that is what actually ties this dossier to a case in the portal.
 */
/** The tied case's name, never a placeholder. HubSpot writes "(no subject)"
 *  on a case with no title, and the loading state has nothing at all — both
 *  read as a client called that. */
function cleanTied(full: string, name: string, ticketId: string): string {
  const f = (full || '').trim();
  if (f && !/\(no subject\)/i.test(f)) return f;
  const n = (name || '').trim();
  return n || `#${ticketId}`;
}

export function DossierModal({ lang, reference, initial, serviceIds, onClose, onSaved }: {
  lang: Lang;
  reference: ActesReference;
  initial?: ActesDossier | null;
  serviceIds?: number[];
  onClose: () => void;
  onSaved?: (id: string) => void;
}) {
  const T = (fr: string, en: string) => (lang === 'fr' ? fr : actesEn(en));
  const qc = useQueryClient();
  const editing = !!initial;
  const [killing, setKilling] = useState(false);

  const [name, setName] = useState(initial?.name ?? '');
  const [statusId, setStatusId] = useState<number | null>(
    initial?.status_id
    ?? reference.dossierStatuses.find((s) => s.code === 'non_commence')?.id
    ?? reference.dossierStatuses[0]?.id
    ?? null,
  );
  /* Whoever is creating it is the referring lawyer until they say otherwise.
     The portal knows who is signed in — it resolved the e-mail to this Actes
     profile — so leaving the field empty and hoping somebody fills it in later
     is how a dossier ends up with nobody's name on it. */
  const [lead, setLead] = useState<string>(initial?.lead_lawyer_id ?? (initial ? '' : reference.me.id));
  const [services, setServices] = useState<number[]>(serviceIds ?? []);
  const [members, setMembers] = useState<string[]>([]);
  const [drive, setDrive] = useState(initial?.drive_url ?? '');
  const [hsId, setHsId] = useState(initial?.hubspot_id ?? '');
  const [hsUrl, setHsUrl] = useState(initial?.hubspot_url ?? '');
  const [parallel, setParallel] = useState(initial?.parallel_url ?? '');
  const [priority, setPriority] = useState<number | ''>(initial?.priority ?? '');
  const [applicants, setApplicants] = useState<number | ''>(initial?.quantite_demandeurs ?? '');
  const [submission, setSubmission] = useState(initial?.submission_date ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [svcOpen, setSvcOpen] = useState(false);
  const [memOpen, setMemOpen] = useState(false);
  /* A dossier tied through the legacy `hubspot_id` alone is tied all the same:
     every reader in Actes says so (`ticket_id ?? hubspot_id`). Seeding from
     one column only meant such a dossier opened its edit form asking "which
     case?" while printing the case number thirty lines lower. */
  const [ticketId, setTicketId] = useState<string>(initial?.ticket_id ?? initial?.hubspot_id ?? '');
  const [clientQ, setClientQ] = useState('');
  const [hits, setHits] = useState<TicketMatch[]>([]);
  /* Three states for one question — "which case is this?": still choosing,
     tied to a case, or deliberately none. The third one is a real answer:
     a dossier can exist before its case does, and the form must be able to
     say so instead of leaving the block hanging. */
  const [noTicket, setNoTicket] = useState(false);
  /* Pressed "change": the search comes back for this one edit, without having
     to detach first and risk saving a dossier with no case at all. */
  const [swapTicket, setSwapTicket] = useState(false);
  /* THE CASE, AS IT IS ACTUALLY CALLED. Editing a dossier used to print the
     dossier's own stored name — which, on a dossier created before the model
     had read that subject, IS the mangled filing string, saved for good. The
     ticket is fetched and read here so the row says the client and the
     service, whatever was stored. */
  /**
   * QUI RESSEMBLE À CE NOM, PARMI LES DOSSIERS QUI EXISTENT.
   *
   * On compare sur les mots, pas sur la chaîne entière : « Chris NOWERS » doit
   * retrouver « NOWERS Chris » et « Nowers — Citizenship by Descent ». Les
   * accents et la casse ne comptent pas, et il faut au moins un mot de trois
   * lettres en commun — sinon toute la base « ressemble ».
   */
  const allDossiers = useQuery({
    queryKey: ['actes', 'dossiers'],
    queryFn: ({ signal }) => actesDossiers(signal),
    staleTime: 60_000,
    enabled: !editing,
  });
  const lookalikes = useMemo(() => {
    const fold = (v: string) => v.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    const words = fold(name).split(/[^a-z0-9]+/).filter((w) => w.length >= 3);
    if (words.length === 0) return [];
    return (allDossiers.data ?? [])
      .filter((d: ActesDossier) => !d.closed_at)
      .map((d: ActesDossier) => {
        const t = fold(d.name || '');
        return { d, hits: words.filter((w) => t.includes(w)).length };
      })
      .filter((x: { d: ActesDossier; hits: number }) => x.hits > 0 && x.hits >= Math.min(words.length, 2))
      .sort((a: { hits: number }, b: { hits: number }) => b.hits - a.hits)
      .slice(0, 3)
      .map((x: { d: ActesDossier }) => x.d);
  }, [allDossiers.data, name, editing]);

  const tiedTicket = useQuery({
    queryKey: ['hs-ticket', ticketId],
    queryFn: () => fetchTicketById('FR', ticketId),
    enabled: !!ticketId,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const tidy = useTidyName(tiedTicket.data);
  /* The wide net, only when the narrow one came back empty. */
  const [deep, setDeep] = useState(false);
  const [searched, setSearched] = useState(false);
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  /* THE CLIENT'S CASE, not a form to retype. Picking it brings the name, the
     two links and the tie itself — the dossier and the case become the same
     procedure seen twice, which is the whole point of the fusion. */
  /* Few answers, on purpose. A list of twenty is not a choice, it is a second
     search: the first pass keeps the FOUR whose client name really carries
     what was typed, ordered by how squarely they match. A pure number goes
     straight to that HubSpot id, which is the way out when the name does not
     agree with what HubSpot has.
     `deep` widens it — the same query, but reading the subject and the owner
     too, and it still hands back at most three. */
  const findClient = async (wide = false) => {
    const q = clientQ.trim();
    if (q.length < 2) return;
    setErr(''); setReading(true); setDeep(wide);
    try {
      const all = await searchTicketsSmart('FR', q);
      if (/^\d{5,}$/.test(q)) { setHits(all.slice(0, 4)); setSearched(true); setReading(false); return; }

      const fold = (x: string) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      const words = fold(q).split(/[^a-z0-9]+/).filter((w) => w.length > 1);
      const score = (t: TicketMatch): number => {
        const near = fold(t.client || prettySubject(t.subject));
        const far = wide ? fold([t.subject, t.ownerName, t.service, t.pipelineLabel].filter(Boolean).join(' ')) : '';
        let n = 0;
        for (const w of words) {
          if (near.includes(w)) n += 2;
          else if (far.includes(w)) n += 1;
        }
        /* The whole thing, in order, beats the same words scattered. */
        if (near.includes(fold(q))) n += 3;
        return n;
      };
      const ranked = all.map((t) => ({ t, n: score(t) })).filter((x) => x.n > 0)
        .sort((a, b) => b.n - a.n).map((x) => x.t);
      setHits(ranked.slice(0, wide ? 3 : 4));
      setSearched(true);
    }
    catch (e) { setErr(friendlyError(e, lang)); }
    setReading(false);
  };

  const takeTicket = async (t: TicketMatch) => {
    /* Read BEFORE the flag is cleared: whether this is a first link or a
       change decides which fields may be overwritten. */
    const swapping = swapTicket && !!ticketId && ticketId !== t.id;
    setHits([]); setClientQ(''); setNoTicket(false); setSearched(false); setSwapTicket(false);
    setTicketId(t.id);
    /* Fill what is EMPTY, never what is written. On an existing dossier the
       name is already right and the links may have been pasted by hand; the
       point of joining the case is to complete the record, not to redo it. */
    /* The dossier is named after the CLIENT. When the model has not read this
       subject yet, the shared cleaner strips the codes and the brackets — a
       dossier called "Nowers_Citizenship by Descent _France_ - (Citizenship)"
       is a dossier nobody can find again. */
    /* THE NAME IS SAVED, so it has to be right the FIRST time. A dossier
       named from the heuristic ("Nowers Citizenship by Descent France —
       Citizenship") carries that string for ever: it is what the list shows,
       what the search matches, and what every later edit prints back at you.
       So when the search result does not already carry the read name, it is
       asked for and WAITED for — one call, once, at the only moment it
       matters. */
    if (!name.trim()) {
      let client = (t.client || '').trim();
      if (!client) {
        try {
          const r = await fetch(`/api/hubspot?tidy=${encodeURIComponent(t.subject.slice(0, 300))}`,
            { headers: await authHeaders() });
          if (r.ok) client = ((await r.json()) as { client?: string }).client?.trim() || '';
        } catch { /* the cleaner below is still better than the raw subject */ }
      }
      setName(client || prettySubject(t.subject));
    }
    /* Filling only what is EMPTY is right for a FIRST link — somebody may
       have pasted these by hand. It is wrong for a CHANGE: keeping the
       previous case's HubSpot id and its two URLs left the dossier pointing
       at one case by ticket_id and at another by everything else. When this
       is a swap, the case's own fields are replaced. */
    if (swapping) { setHsId(t.id); setHsUrl(''); setParallel(''); }
    else setHsId((v) => v || t.id);
    setErr('');
    /* The full ticket carries the two URLs and the Parallel id, which the
       search result does not. */
    try {
      const full = await fetchTicketById('FR', t.id);
      if (full) {
        if (swapping) { setHsUrl(full.url || ''); setParallel(full.parallelUrl || ''); }
        else {
          setHsUrl((v) => v || full.url || '');
          setParallel((v) => v || full.parallelUrl || '');
        }

      }
    } catch { /* the tie is already made; the links are a bonus */ }
  };


  /* ── CE QUE LA LISTE DOIT MONTRER EN PREMIER ──
     Une recherche sur « Tsiantar » remonte quatre lignes : deux tests
     d'éligibilité, une citoyenneté en rédaction, une citoyenneté en attente de
     pièces. Rattacher un dossier d'ACTES à un test d'éligibilité n'a pas de
     sens : ce qu'on veut, c'est le dossier de nationalité. On classe donc par
     ce que le dossier EST — CNF et nationalité devant, transcription et
     relocation derrière — et à égalité on garde l'ordre de HubSpot.
     Rien n'est caché : c'est un ordre, pas un filtre. */
  const NATIONALITY = /\b(cnf|nationalit|citizenship|naturalisation|naturalization|passeport|passport)\b/i;
  const SIDE = /\b(transcription|relocation|reloc|visa|d[ée]m[ée]nagement|traduction|apostille)\b/i;
  const rankTicket = (t: TicketMatch): number => {
    const hay = [t.service, t.subject, t.pipelineLabel].filter(Boolean).join(' ');
    if (NATIONALITY.test(hay)) return 0;
    if (SIDE.test(hay)) return 2;
    return 1;
  };
  /* Le MÊME dossier revenait deux fois quand HubSpot le renvoyait deux fois :
     on garde la première occurrence de chaque identifiant. */
  const ranked = (list: TicketMatch[]): TicketMatch[] => {
    const seen = new Set<string>();
    const once = list.filter((t) => (seen.has(t.id) ? false : (seen.add(t.id), true)));
    return once.map((t, i) => ({ t, i })).sort((a, b) => rankTicket(a.t) - rankTicket(b.t) || a.i - b.i).map((x) => x.t);
  };

  const toggle = <T,>(list: T[], v: T): T[] => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const kill = async () => {
    if (!initial) return;
    setBusy(true); setErr('');
    try {
      await actesDossierLifecycle(initial.id, 'delete');
      await qc.invalidateQueries({ queryKey: ['actes'] });
      onSaved?.(initial.id);
      onClose();
    } catch (e) { setErr(friendlyError(e, lang)); setKilling(false); }
    setBusy(false);
  };

  const save = async () => {
    if (!name.trim()) { setErr(T('Le nom est obligatoire.', 'The name is required.')); return; }
    setBusy(true); setErr('');
    try {
      const body = {
        name: name.trim(),
        status_id: statusId,
        lead_lawyer_id: lead || null,
        drive_url: drive.trim() || null,
        /* ── UN SEUL LIEN, QUATRE CHAMPS ──
           The tie to a case is recorded in four places (the portal's own
           ticket_id, the legacy HubSpot order number, and the two URLs), and
           each was written on its own. So "Aucun dossier client" showed a
           "no case" card and SAVED the old tie anyway — the screen said one
           thing and the row said another. They move together now: saying
           there is no case clears all four. */
        hubspot_id: noTicket ? null : (hsId.trim() || null),
        hubspot_url: noTicket ? null : (hsUrl.trim() || null),
        parallel_url: noTicket ? null : (parallel.trim() || null),
        ticket_id: noTicket ? null : (ticketId || null),
        priority: priority === '' ? null : priority,
        quantite_demandeurs: applicants === '' ? null : applicants,
        submission_date: submission || null,
        notes: notes.trim() || null,
        service_ids: services,
        ...(editing ? {} : { member_ids: members }),
      };
      const id = editing
        ? (await actesUpdateDossier(initial!.id, body), initial!.id)
        : (await actesCreateDossier(body)).data.id;
      /* Les demandes proposées par Parallel se créent maintenant depuis
         « Depuis Parallel », qui est le seul écran qui lit le formulaire. */
      await qc.invalidateQueries({ queryKey: ['actes'] });
      onSaved?.(id);
      onClose();
    } catch (e) {
      setErr(friendlyError(e, lang));
    }
    setBusy(false);
  };

  return createPortal(
    <div className="scrim actes-skin" onClick={onClose}>
      <div className="modal wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-h">
          <div>
            <div className="eyebrow">{T('Dossier', 'Dossier')}</div>
            <h3>{editing ? T('Modifier le dossier', 'Edit dossier') : T('Nouveau dossier', 'New dossier')}</h3>
          </div>
          <button className="rb x" onClick={onClose} aria-label={T('Fermer', 'Close')}>
            <KBIcon name="x" size={14} />
          </button>
        </div>
        <div className="modal-b">

          {/* ── LE DOSSIER CLIENT ──
              Shown when creating AND when editing. A dossier very often exists
              before its case does — the family starts gathering certificates
              long before anything is opened in HubSpot — so joining the two
              later has to be possible, and it was not. Picking the case fills
              every field that is still EMPTY (the HubSpot id and link, the
              Parallel link and id) and never overwrites what somebody typed. */}
          {(
            <fieldset className="actes-fieldset">
              <span className="cap">{T('Le dossier client', 'The client’s case')}</span>
              {ticketId && !swapTicket ? (
                <div className="actes-chosen">
                  <KBIcon name="case" size={13} />
                  <span className="actes-list-main">
                    {/* While the ticket is still loading — or if it fails —
                        the row said "(no subject)", which HubSpot uses for a
                        case with no title and which reads as the client's
                        name. The dossier's own name, then the number. */}
                    <strong>{cleanTied(tidy.full, name, ticketId)}</strong>
                    <em>{T('Rattaché au dossier client', 'Tied to the case')} · #{ticketId}</em>
                  </span>
                  {/* From here the case is somewhere else, so the link is real
                      and it works: it asks the portal to open that case and
                      closes this form. */}
                  <button type="button" onClick={() => {
                    window.dispatchEvent(new CustomEvent('lexidy:open-case', { detail: { id: ticketId } }));
                    onClose();
                  }}>{T('ouvrir', 'open')}</button>
                  {/* Change, not detach-then-search: a dossier tied to the
                      wrong case must never have to pass through "tied to
                      nothing" to be corrected. */}
                  <button type="button" className="alt" onClick={() => { setSwapTicket(true); setSearched(false); setHits([]); }}>
                    {T('changer', 'change')}
                  </button>
                  {/* Detaching used to clear ticket_id and leave hubspot_id
                      behind — and every reader in Actes resolves the tie as
                      `ticket_id ?? hubspot_id`, so the dossier stayed attached
                      to a case it had just been detached from. All four go. */}
                  <button type="button" className="alt danger" onClick={() => {
                    setTicketId(''); setSearched(false);
                    setHsId(''); setHsUrl(''); setParallel('');
                  }}>
                    {T('détacher', 'detach')}
                  </button>
                </div>
              ) : noTicket ? (
                <div className="actes-chosen">
                  <KBIcon name="x" size={13} />
                  <span className="actes-list-main">
                    <strong>{T('Sans dossier client', 'No case assigned')}</strong>
                    <em>{T('Le dossier se crée seul ; il pourra être rattaché plus tard.',
                          'The dossier stands on its own; it can be tied to a case later.')}</em>
                  </span>
                  <button onClick={() => setNoTicket(false)}>{T('chercher', 'search')}</button>
                </div>
              ) : (
                <>
                  <div className="actes-searchline">
                    <label className="actes-find wide">
                      <KBIcon name="search" size={14} />
                      <input value={clientQ} onChange={(e) => { setClientQ(e.target.value); setSearched(false); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') void findClient(false); }}
                        placeholder={T('Nom du client ou identifiant HubSpot…', 'Client name or HubSpot id…')} />
                      {clientQ && <button onClick={() => { setClientQ(''); setHits([]); setSearched(false); }} aria-label="clear">×</button>}
                    </label>
                    <button className="btn btn-sm" disabled={reading || clientQ.trim().length < 2} onClick={() => void findClient(false)}>
                      {reading ? '…' : T('Chercher', 'Search')}
                    </button>
                  </div>

                  {ranked(hits).map((t) => (
                    <button key={t.id} className="actes-suggest-row" onClick={() => void takeTicket(t)}>
                      <KBIcon name="case" size={13} />
                      <span className="actes-list-main">
                        {/* The AI-read client name, never the raw filing
                            subject — that string is how HubSpot files a case,
                            not how a person recognises one. */}
                        <strong>{t.client || prettySubject(t.subject)}</strong>
                        {/* The service the model read, the stage, the owner.
                            NOT the pipeline label — "FR - Nationality/Citizenship"
                            is how the database files it, not how anybody says
                            it; better a shorter line than that. */}
                        <em>{[t.service, t.stageLabel, t.ownerName].filter(Boolean).join(' · ')}</em>
                      </span>
                      {/* LE NUMÉRO, VISIBLE. Deux lignes se ressemblent souvent
                          au mot près — même client, même service, deux étapes.
                          Le numéro est ce qui les distingue vraiment, et c'est
                          lui qu'on ira comparer dans HubSpot. */}
                      <span className="actes-num">#{t.id}</span>
                      <KBIcon name="chevron-right" size={13} />
                    </button>
                  ))}

                  {searched && hits.length === 0 && !reading && (
                    <p className="muted-note">
                      {deep
                        ? T('Rien, même en cherchant large.', 'Nothing, even searching wide.')
                        : T('Aucun dossier client ne porte ce nom.', 'No case carries that name.')}
                    </p>
                  )}

                  <div className="actes-searchline">
                    {searched && !deep && (
                      <button className="btn btn-sm btn-ghost" disabled={reading} onClick={() => void findClient(true)}>
                        <KBIcon name="search" size={13} /> {T('Recherche avancée', 'Advanced search')}
                      </button>
                    )}
                    {/* Saying "no case" is a decision, so it takes effect:
                        the four fields go with it (see save()). */}
                    <button className="btn btn-sm btn-ghost" onClick={() => {
                      setNoTicket(true); setHits([]); setTicketId('');
                    }}>
                      {T('Aucun dossier client', 'No case assigned')}
                    </button>
                    {swapTicket && ticketId && (
                      <button className="btn btn-sm btn-ghost" onClick={() => { setSwapTicket(false); setHits([]); setSearched(false); }}>
                        {T('garder celui d’avant', 'keep the previous one')}
                      </button>
                    )}
                  </div>

                  <p className="muted-note">
                    {T('Choisir le dossier client remplit le nom et les liens, et lie les deux pour de bon. Sans dossier client, tout se saisit à la main.',
                      'Picking the case fills the name and the links, and ties the two for good. With no case, everything is typed by hand.')}
                  </p>
                </>
              )}
            </fieldset>
          )}

          <label className="fl">
            <span>{T('Nom du client', 'Client name')}</span>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus
              placeholder={T('Prénom NOM', 'First LAST')} />
          </label>

          {/* ── UN DOSSIER POUR CETTE FAMILLE EXISTE PEUT-ÊTRE DÉJÀ ──
              C'est l'accident normal de cette section : la famille a commencé
              à réunir des actes bien avant qu'on ouvre quoi que ce soit, et un
              deuxième dossier au même nom est pire que pas de dossier du tout
              — les actes finissent répartis entre les deux et personne ne voit
              jamais le compte complet. On regarde pendant qu'on tape, on ne
              bloque rien, et on propose d'ouvrir celui qui existe. */}
          {!editing && lookalikes.length > 0 && (
            <div className="actes-dup">
              <span className="cap">
                {T(lookalikes.length === 1 ? 'Un dossier porte déjà ce nom' : `${lookalikes.length} dossiers portent déjà ce nom`,
                  lookalikes.length === 1 ? 'A dossier already carries this name' : `${lookalikes.length} dossiers already carry this name`)}
              </span>
              {lookalikes.map((d) => (
                <button key={d.id} type="button" className="actes-dup-row2"
                  onClick={() => { onSaved?.(d.id); onClose(); }}>
                  <KBIcon name="documents" size={13} />
                  <span className="actes-list-main">
                    <strong>{d.name}</strong>
                    <em>
                      {dossierStatusLabel(reference, d) || '—'}
                      {' · '}
                      {d.actes_received ?? 0}/{d.actes_total ?? 0} {T('actes', 'records')}
                      {d.lead_lawyer_name ? ` · ${d.lead_lawyer_name}` : ''}
                    </em>
                  </span>
                  <span className="actes-dup-open">{T('ouvrir', 'open')}</span>
                </button>
              ))}
              <p className="muted-note">
                {T('Si c’est bien la même famille, ouvrez-le plutôt que d’en créer un second.',
                  'If it is the same family, open that one instead of creating a second.')}
              </p>
            </div>
          )}

          <div className="fl">
            <span>{T('Service', 'Service')}</span>
            <div className="actes-picks">
              {reference.services.filter((s) => services.includes(s.id)).map((s) => (
                <span key={s.id} className="actes-pick actes-skin">
                  {lang === 'fr' ? s.label_fr : s.label_en}
                  <button onClick={() => setServices((l) => toggle(l, s.id))} aria-label="remove">×</button>
                </span>
              ))}
              <div className="actes-pick-anchor">
                <button className="actes-pick-add" onClick={() => setSvcOpen((o) => !o)}>
                  <KBIcon name="plus" size={12} /> {T('Ajouter', 'Add')}
                </button>
                {svcOpen && (
                  <>
                    <div className="mw-dd-scrim" onClick={() => setSvcOpen(false)} />
                    <div className="actes-pick-menu">
                      {reference.services.filter((s) => !services.includes(s.id)).map((s) => (
                        <button key={s.id} onClick={() => { setServices((l) => toggle(l, s.id)); setSvcOpen(false); }}>
                          {lang === 'fr' ? s.label_fr : s.label_en}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="fgrid">
            <label className="fl">
              <span>{T('Statut', 'Status')}</span>
              <select value={statusId ?? ''} onChange={(e) => setStatusId(e.target.value ? Number(e.target.value) : null)}>
                {reference.dossierStatuses.map((s) => (
                  <option key={s.id} value={s.id}>{lang === 'fr' ? s.label_fr : s.label_en}</option>
                ))}
              </select>
            </label>
            <label className="fl">
              <span>{T('Avocat référent', 'Lead lawyer')}</span>
              {/* Lawyers only. The dossier page has said so since it was
                  ported and this form listed the whole team, so a paralegal
                  could be filed as the referring lawyer — the two roles are
                  not interchangeable, which is exactly why the collaborators
                  field exists below. Somebody already set as the lead stays
                  offered even if their flag says otherwise, so editing an old
                  dossier never silently drops its own referent. */}
              <select value={lead} onChange={(e) => setLead(e.target.value)}>
                <option value="">—</option>
                {reference.profiles
                  .filter((p) => p.is_lawyer || p.id === lead)
                  .map((p) => (
                    <option key={p.id} value={p.id}>{p.full_name ?? p.email}</option>
                  ))}
              </select>
            </label>
          </div>

          {!editing && (
            <div className="fl">
              <span>{T('Collaborateurs', 'Collaborators')}</span>
              <div className="actes-picks">
                {reference.profiles.filter((p) => members.includes(p.id)).map((p) => (
                  <span key={p.id} className="actes-pick actes-skin">
                    {p.full_name ?? p.email}
                    <button onClick={() => setMembers((l) => toggle(l, p.id))} aria-label="remove">×</button>
                  </span>
                ))}
                <div className="actes-pick-anchor">
                  <button className="actes-pick-add" onClick={() => setMemOpen((o) => !o)}>
                    <KBIcon name="plus" size={12} /> {T('Ajouter', 'Add')}
                  </button>
                  {memOpen && (
                    <>
                      <div className="mw-dd-scrim" onClick={() => setMemOpen(false)} />
                      <div className="actes-pick-menu">
                        {reference.profiles.filter((p) => !members.includes(p.id)).map((p) => (
                          <button key={p.id} onClick={() => { setMembers((l) => toggle(l, p.id)); setMemOpen(false); }}>
                            {p.full_name ?? p.email}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="fgrid">
            <label className="fl">
              <span>{T('Priorité', 'Priority')}</span>
              <select value={priority} onChange={(e) => setPriority(e.target.value ? Number(e.target.value) : '')}>
                <option value="">—</option>
                <option value={1}>{T('1 — haute', '1 — high')}</option>
                <option value={2}>{T('2 — moyenne', '2 — medium')}</option>
                <option value={3}>{T('3 — basse', '3 — low')}</option>
              </select>
            </label>
            <label className="fl">
              <span>{T('Nombre de demandeurs', 'Applicants')}</span>
              <input type="number" min={1} value={applicants}
                onChange={(e) => setApplicants(e.target.value ? Number(e.target.value) : '')} />
            </label>
          </div>

          <label className="fl">
            <span>{T('Date de dépôt', 'Submission date')}</span>
            <input type="date" value={submission} onChange={(e) => setSubmission(e.target.value)} />
          </label>

          <label className="fl">
            <span>{T('Notes', 'Notes')}</span>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>

          {/* The doors out. The HubSpot order id is not decoration: it is what
              ties this dossier to its case in the portal, on both sides. */}
          <div className="fl">
            <span>{T('Liens externes', 'External links')}</span>
            <div className="fgrid">
              <input value={hsId} onChange={(e) => setHsId(e.target.value)} placeholder={T('N° de commande HubSpot', 'HubSpot order id')} />
              <input value={hsUrl} onChange={(e) => setHsUrl(e.target.value)} placeholder="https://app.hubspot.com/…" />
              <input value={parallel} onChange={(e) => setParallel(e.target.value)} placeholder="https://…parallel…" />
              <input value={drive} onChange={(e) => setDrive(e.target.value)} placeholder="https://drive.google.com/…" />
            </div>
          </div>

          {/* LE CHEMIN PARALLEL EST PARTI, ET C'EST VOULU.
              Il vivait ici ET dans « Depuis Parallel » : deux portes vers la
              même pièce, et on finissait avec deux dossiers pour une famille.
              Tout Parallel — l'identifiant, le numéro de dossier client et le
              PDF — est maintenant derrière le bouton « Depuis Parallel » de la
              liste des dossiers. Ici on crée un dossier à la main. */}

          {err && <p className="muted-note ni-err">⚠ {err}</p>}
        </div>

        {/* `.modal-f` comme la maquette : la corbeille à gauche, l'action à
            droite, le `.spacer` entre les deux. */}
        <div className="modal-f">
            {/* METTRE UN DOSSIER À LA CORBEILLE, DEPUIS LÀ OÙ ON L'ÉDITE.
                Une demande se jette depuis sa fiche ; un dossier, non — il
                fallait le laisser là ou demander à quelqu'un. Et la question
                se pose DANS le bouton : « Corbeille » puis « Sûr ? » côte à
                côte, plutôt qu'une boîte de dialogue par-dessus une autre.
                Rien n'est perdu : la corbeille garde le dossier et ses
                demandes, et « Restaurer » les ramène ensemble. */}
            {editing && reference.me.isStaff && (
              killing ? (
                <span className="actes-killrow">
                  <em>{T('Mettre à la corbeille ?', 'Move to the trash?')}</em>
                  <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setKilling(false)}>
                    {T('Non', 'No')}
                  </button>
                  <button className="btn btn-sm btn-ghost danger" disabled={busy} onClick={() => void kill()}>
                    {T('Oui, à la corbeille', 'Yes, trash it')}
                  </button>
                </span>
              ) : (
                <button className="btn danger" disabled={busy} onClick={() => setKilling(true)}>
                  <KBIcon name="trash" size={13} /> {T('Corbeille', 'Trash')}
                </button>
              )
            )}
            <span className="spacer" />
            <button className="btn" onClick={onClose}>{T('Annuler', 'Cancel')}</button>
            <button className="btn btn-p" disabled={busy || !name.trim()} onClick={() => void save()}>
              {busy ? '…' : editing ? T('Enregistrer', 'Save') : T('Créer le dossier', 'Create dossier')}
            </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
